import React, { Component, ReactNode } from 'react';
import './AccountSettings.scss';
import { DashboardLayout } from '../layouts/dashboard-layout/DashboardLayout';
import AccountInfo from '../features/account-settings/AccountInfo';
import Button from 'react-bootstrap/Button';
import ChangeAvatar from '../features/account-settings/ChangeAvatar';
import { PasswordUpdateForm } from '../features/password-update-form/PasswordUpdateForm';
import Alert from 'react-bootstrap/Alert';
import jwtDecode from 'jwt-decode';
import { AuthHelper } from '../helpers/AuthHelper';
import { store, ApplicationState } from '../app/store';
import { connect } from 'react-redux';
import { SingleUser } from '../api/types';
import { IDossierV2 } from '../models/DossierV2';
import { ERouterPath } from '../features/router/ERouterPath';
import { TrustedDevices } from '../features/trusted-devices/TrustedDevices';

interface AccountSettingsProps {
  file?: IDossierV2;
  user?: SingleUser;
}

interface AccountSettingsState {
  showPasswordForm?: boolean
  passwordUpdated?: boolean
  fullName: string;
  username: string;
  email: string;
  mobileMode: boolean;
}

class AccountSettings extends Component<AccountSettingsProps, AccountSettingsState> {
  constructor(props: AccountSettingsProps) {
    super(props);
    this.state = {
      username: 'Laden...',
      fullName: 'Laden...',
      email: 'Laden...',
      mobileMode: true,
    };
  }

  componentDidMount() {
    // eslint-disable-next-line no-void
    void this.loadUserInfoFromToken();
    window.addEventListener('resize', this.resizeUpdate);
    this.resizeUpdate(); // Init
  }

  componentWillUnmount(): void {
    window.removeEventListener('resize', this.resizeUpdate);
  }

  private resizeUpdate = () => {
    const { innerWidth } = window;
    const isMobileMode = innerWidth <= 480; // 480 = $mobile-breakpoint in _library.scss
    if (isMobileMode !== this.state.mobileMode) {
      this.setMobileMode(isMobileMode);
    }
  };

  private setMobileMode = (isMobileMode: boolean) => {
    this.setState({ mobileMode: isMobileMode });
  };

  async loadUserInfoFromToken(): Promise<void> {
    const { user } = store.getState().session;
    try {
      const fullName = user?.name;
      const token = await AuthHelper.getIdJwtToken();
      /* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access */
      const decodedToken: any = jwtDecode(token);
      const username = decodedToken['cognito:username'] as string;
      const email = decodedToken.email as string;
      /* eslint-enable */
      this.setState({
        username: username ? username : 'Onbekend',
        fullName: fullName ? fullName : 'Onbekend',
        email: email ? email : 'Onbekend'
      });
    } catch (err) {
      this.setState({
        username: 'Laden mislukt!',
        fullName: 'Laden mislukt!',
        email: 'Laden mislukt!'
      });
    }
  }

  handleClickChangePassword = (): void => {
    this.setState({ showPasswordForm: !this.state.showPasswordForm, passwordUpdated: false });
  };

  handlePasswordUpdated = (): void => {
    this.setState({ showPasswordForm: false, passwordUpdated: true }, this.onChangeSetting);
  };

  onChangeSetting = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    const { showPasswordForm, passwordUpdated, fullName, username, email } = this.state;
    const { user } = this.props;
    const { mobileMode } = this.state;
    return (
      <DashboardLayout size="small" hasBackGround={true} noTabs={true} backButton={ERouterPath.dossier}>
        <div className="account-settings">
          <div className="account-settings__content">
            <h2 className="account-settings__title">Mijn profiel</h2>
            <p className="account-settings__info">
              Hier zie je jouw gegevens die worden gebruikt binnen de MijnTOPP-omgeving.
              Je hebt de mogelijkheid om een nieuw wachtwoord in te stellen, of om een
              (andere) profielfoto in te stellen.
            </p>
          </div>
          {
            !mobileMode &&
            <ChangeAvatar userId={user?.id} onChangeAvatar={this.onChangeSetting} className="account-settings__avatar"/>
          }
          <div className="account-settings__settings-container">
            <div className="account-settings__main-settings">
              <AccountInfo
                className="account-settings__account-info"
                fullName={fullName} username={username} email={email}
              />
              {
                mobileMode &&
                <ChangeAvatar userId={user?.id} onChangeAvatar={this.onChangeSetting} className="account-settings__avatar"/>
              }
            </div>
            {
              !showPasswordForm &&
              <>
                <Button onClick={this.handleClickChangePassword}>Wachtwoord wijzigen</Button>
                { passwordUpdated && <Alert variant="success">Jouw wachtwoord is gewijzigd.</Alert> }
              </>
            }
            {
              showPasswordForm && (
                <div className="account-settings__password-update-box">
                  <PasswordUpdateForm
                    onUpdated={this.handlePasswordUpdated}
                    alignRight={true}
                  />
                </div>
              )
            }
          </div>
          <TrustedDevices />
        </div>
      </DashboardLayout>
    );
  }
}

export default connect((state: ApplicationState) => ({
  dossier: state.session.dossier,
  user: state.session.user,
}))(AccountSettings);
